import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import {
  adminApi,
  type KickStatus,
  type SubiektStatus,
  type TestDonationResult,
  type TestInvoiceResult,
} from "@/lib/admin";

// Browser-driven OAuth start. Both endpoints use 302-redirect to the provider's
// auth page; the callback writes the token row to DB. Must navigate the WHOLE
// window — fetch() with credentials won't preserve the redirect chain back
// through Set-Cookie.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "";
const KICK_BROADCASTER_CONNECT = `${API_BASE}/api/auth/kick/admin/connect`;
const SUBIEKT_CONNECT = `${API_BASE}/api/auth/subiekt/login`;

export function AdminDiagnostics() {
  return (
    <div className="space-y-8 max-w-4xl">
      <header>
        <h1 className="font-display font-bold text-3xl">Diagnostyka integracji</h1>
        <p className="text-sm text-carp-ink/70 mt-1">
          Testowe wywołania kluczowych flow + snapshoty stanu integracji. Test
          fakturowy idzie do prawdziwego Subiekta — używaj z głową.
        </p>
      </header>

      <ConnectFlowsSection />
      <TestDonationSection />
      <TestInvoiceSection />
      <KickStatusSection />
      <SubiektStatusSection />
    </div>
  );
}

// ─── Connect flows (one-time OAuth init) ───────────────────────
function ConnectFlowsSection() {
  return (
    <section className="brut bg-carp-yellow p-5 space-y-3">
      <header>
        <h2 className="font-display font-bold text-xl">Inicjacja integracji (jednorazowo)</h2>
        <p className="text-xs text-carp-ink/70 mt-1">
          Tokeny w tabelach <code className="font-mono">KickTokens</code> i{" "}
          <code className="font-mono">SubiektTokens</code> powstają dopiero po
          przejściu OAuth flow z danym providerem. Refresh dzieje się potem
          automatycznie — przy próbie subscribe (Kick) lub przy próbie wystawienia
          faktury (Subiekt) — o ile refresh_token jest jeszcze ważny.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <a
          href={KICK_BROADCASTER_CONNECT}
          className="btn-primary text-center"
        >
          ▶ Połącz Kick (broadcaster)
        </a>
        <a
          href={SUBIEKT_CONNECT}
          className="btn-primary text-center"
        >
          ▶ Połącz Subiekt 123
        </a>
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer font-display font-bold uppercase">
          Co dokładnie się dzieje?
        </summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1 font-mono">
          <li>Klikasz przycisk → backend buduje URL OAuth + PKCE → redirect do providera.</li>
          <li>Logujesz się u providera, akceptujesz scope'y.</li>
          <li>Provider redirectuje na <code>/api/auth/&lt;provider&gt;/callback</code> z code.</li>
          <li>
            Backend wymienia code na <b>access_token + refresh_token</b> → zapisuje wiersz
            w odpowiedniej tabeli (KickTokens / SubiektTokens).
          </li>
          <li>
            Dla Kicka: po tym zrób też <code>POST /api/auth/kick/subscribe?kickUserId=...</code>{" "}
            żeby aktywować subscriptions (Kick API doc).
          </li>
        </ol>
      </details>
    </section>
  );
}

// ─── Test donation ─────────────────────────────────────────────
function TestDonationSection() {
  const auth = useAuth();
  const [form, setForm] = useState({
    donorName: "",
    amount: 25,
    message: "Testowa wiadomość — sprawdzam czy karp gada.",
  });
  const [result, setResult] = useState<TestDonationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Prefill donor name from Kick login (admin's own username) until they edit it.
  const authUser = auth.loading ? null : auth.user;
  useEffect(() => {
    if (!authUser) return;
    setForm((f) => (f.donorName ? f : { ...f, donorName: authUser.kickUsername }));
  }, [authUser]);

  async function fire() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await adminApi.testDonation({
        donorName: form.donorName || undefined,
        amount: form.amount > 0 ? form.amount : undefined,
        message: form.message || undefined,
      });
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="brut bg-carp-paper p-5 space-y-3">
      <header>
        <h2 className="font-display font-bold text-xl">Testowa wpłata + odpowiedź</h2>
        <p className="text-xs text-carp-ink/60 mt-1">
          Pełen flow: Phase 1 (głos czytany przez ElevenLabs) → Phase 2 (Claude → ElevenLabs streamed → ESP).
          Nie przechodzi przez Stripe.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="td-donor" className="label-brut">Donor (nick)</label>
          <input
            id="td-donor"
            className="input-brut"
            value={form.donorName}
            onChange={(e) => setForm((f) => ({ ...f, donorName: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="td-amount" className="label-brut">Kwota (PLN)</label>
          <input
            id="td-amount"
            type="number"
            min={1}
            step={1}
            className="input-brut font-mono"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
          />
        </div>
      </div>
      <div>
        <label htmlFor="td-msg" className="label-brut">Wiadomość</label>
        <textarea
          id="td-msg"
          rows={3}
          className="input-brut"
          value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
        />
      </div>

      <div className="pt-2">
        <button type="button" className="btn-primary" disabled={busy} onClick={fire}>
          {busy ? "Lecę…" : "Wyemituj testowy donejt"}
        </button>
      </div>

      {err && <div className="brut bg-carp-destructive text-white p-3 text-sm">{err}</div>}
      {result && (
        <div
          className={`brut p-4 text-sm space-y-2 ${
            result.success ? "bg-carp-lime" : "bg-carp-destructive text-white"
          }`}
        >
          <div className="font-mono">
            <span className="font-display font-bold uppercase">
              {result.success ? "✓ Sukces" : "✗ Błąd"}
            </span>
            <span className="ml-3">{result.connectedDevices} urządzeń · {result.durationMs} ms</span>
          </div>
          {result.phase1Text && (
            <div>
              <p className="font-display font-bold uppercase text-xs">Phase 1 (głos donatora)</p>
              <p className="font-mono whitespace-pre-wrap mt-1">{result.phase1Text}</p>
            </div>
          )}
          {result.responseText && (
            <div>
              <p className="font-display font-bold uppercase text-xs">Phase 2 (MudCarp)</p>
              <p className="font-mono whitespace-pre-wrap mt-1">{result.responseText}</p>
            </div>
          )}
          {result.error && <p className="font-mono"><b>Error:</b> {result.error}</p>}
        </div>
      )}
    </section>
  );
}

// ─── Test invoice (Subiekt) ────────────────────────────────────
function TestInvoiceSection() {
  const auth = useAuth();
  const [form, setForm] = useState({
    email: "",
    customerName: "",
    amount: 1,
  });
  const [result, setResult] = useState<TestInvoiceResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Prefill name+email from Kick login so test invoice goes to the admin running it.
  const authUser = auth.loading ? null : auth.user;
  useEffect(() => {
    if (!authUser) return;
    setForm((f) => ({
      ...f,
      email: f.email || (authUser.email ?? ""),
      customerName: f.customerName || authUser.kickUsername,
    }));
  }, [authUser]);

  async function fire() {
    if (!form.email || form.amount <= 0) {
      setErr("Wymagane: email + kwota > 0");
      return;
    }
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await adminApi.testInvoice({
        email: form.email,
        customerName: form.customerName || undefined,
        amount: form.amount,
      });
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="brut bg-carp-paper p-5 space-y-3">
      <header>
        <h2 className="font-display font-bold text-xl">Testowa faktura (Subiekt 123)</h2>
        <p className="text-xs text-carp-ink/60 mt-1">
          Wystawia REALNĄ fakturę w prod Subiekt na osobę prywatną (imię + mail).
          Nie ma sandboxa — każde udane wywołanie to faktura w systemie.
        </p>
      </header>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor="ti-email" className="label-brut">Email odbiorcy</label>
          <input
            id="ti-email"
            type="email"
            autoComplete="email"
            className="input-brut"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="ti-amount" className="label-brut">Kwota (PLN)</label>
          <input
            id="ti-amount"
            type="number"
            min={1}
            step={1}
            className="input-brut font-mono"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
          />
        </div>
      </div>
      <div>
        <label htmlFor="ti-name" className="label-brut">Imię / nazwa odbiorcy</label>
        <input
          id="ti-name"
          className="input-brut"
          value={form.customerName}
          onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
          placeholder="Jan Testowy"
        />
      </div>

      <div className="pt-2">
        <button type="button" className="btn-primary" disabled={busy} onClick={fire}>
          {busy ? "Wystawiam…" : "Wystaw testową fakturę"}
        </button>
      </div>

      {err && <div className="brut bg-carp-destructive text-white p-3 text-sm">{err}</div>}
      {result && (
        <div
          className={`brut p-4 text-sm space-y-1 ${
            result.success ? "bg-carp-lime" : "bg-carp-destructive text-white"
          }`}
        >
          <p className="font-display font-bold uppercase">
            {result.success ? "✓ Wystawiono" : "✗ Błąd"}
          </p>
          {result.invoiceId && (
            <p className="font-mono">Subiekt InvoiceId: <b>{result.invoiceId}</b></p>
          )}
          <p className="font-mono text-xs">fakeSaleId: {result.fakeSaleId}</p>
          {result.error && <p className="font-mono"><b>Error:</b> {result.error}</p>}
        </div>
      )}
    </section>
  );
}

// ─── Kick status ───────────────────────────────────────────────
function KickStatusSection() {
  const [status, setStatus] = useState<KickStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    setErr(null);
    try {
      setStatus(await adminApi.kickStatus());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <section className="brut bg-carp-paper p-5 space-y-3">
      <header className="flex items-center justify-between gap-3">
        <h2 className="font-display font-bold text-xl">Kick — stan integracji</h2>
        <button type="button" className="btn-primary !py-1 !px-3 text-xs" disabled={busy} onClick={refresh}>
          {busy ? "…" : "Odśwież"}
        </button>
      </header>

      {err && <div className="brut bg-carp-destructive text-white p-2 text-xs">{err}</div>}
      {status && (
        <div className="text-sm space-y-3 font-mono">
          <div className="brut bg-carp-yellow p-3 text-xs">
            <p className="font-display font-bold uppercase">Hint</p>
            <p className="mt-1">{status.hint}</p>
          </div>

          <KV label="Webhook URL (musi być w kick.com dev portal)" value={status.config.webhookUrl || "(BRAK)"} />
          <KV label="Channel slug" value={status.config.channelSlug || "(BRAK)"} />
          <KV label="Client ID skonfigurowany" value={status.config.clientIdConfigured ? "tak" : "NIE"} />

          {status.token.present ? (
            <div className="border-l-4 border-carp-primary pl-3 text-xs space-y-2">
              <p className="font-display font-bold uppercase">Token broadcaster</p>
              <p>user: <b>{status.token.kickUsername}</b> ({status.token.kickUserId})</p>
              <p>issued: {fmtDate(status.token.issuedAt)} · expires: {fmtDate(status.token.expiresAt)}</p>
              <p>expired: {status.token.expired ? "TAK" : "nie"} · revoked: {status.token.revoked ? "TAK" : "nie"}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                <DangerButton
                  label="Deaktywuj (revoke)"
                  confirm={`Oznaczyć token ${status.token.kickUsername} jako revoked? Row zostaje, ale Subscribe i Status go pomijają.`}
                  action={async () => {
                    const r = await adminApi.kickRevokeToken();
                    return `Revoked: ${r.revoked} (${r.kickUserId})`;
                  }}
                  onDone={refresh}
                />
                <DangerButton
                  label="Usuń token z bazy"
                  confirm={`HARD DELETE wiersza KickToken dla ${status.token.kickUsername}? Operacja nieodwracalna — strata historii issued/refreshedAt.`}
                  action={async () => {
                    const r = await adminApi.kickDeleteToken();
                    return `Deleted: ${r.deleted} (${r.kickUserId})`;
                  }}
                  onDone={refresh}
                />
              </div>
            </div>
          ) : (
            <div className="border-l-4 border-carp-destructive pl-3 text-xs space-y-2">
              <p>Token broadcaster: <b>brak</b></p>
              <a href={KICK_BROADCASTER_CONNECT} className="btn-primary inline-block !py-1 !px-3">
                ▶ Połącz Kick teraz
              </a>
            </div>
          )}

          <div className="border-l-4 border-carp-primary pl-3 text-xs space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="font-display font-bold uppercase">Subscriptions ({status.subscriptions.count})</p>
              {status.token.present && !status.token.expired && status.subscriptions.count > 0 && (
                <DangerButton
                  label="Unsubscribe wszystkie"
                  confirm={`Usunąć WSZYSTKIE ${status.subscriptions.count} subskrypcji Kick z bieżącego tokena (${status.token.kickUsername})? Token zostaje, tylko subs są usuwane z Kick API.`}
                  action={async () => {
                    const r = await adminApi.kickUnsubscribeAll();
                    return `Usunięto ${r.deleted}/${r.total}${r.failed.length ? ` · failed: ${r.failed.join(", ")}` : ""}`;
                  }}
                  onDone={refresh}
                />
              )}
            </div>
            {status.subscriptions.error && <p className="text-carp-destructive">Błąd: {status.subscriptions.error}</p>}
            {status.subscriptions.items.length === 0 && !status.subscriptions.error && <p>(brak)</p>}
            {status.subscriptions.items.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 py-0.5">
                <span>· {s.event} v{s.version} ({s.method})</span>
                <DangerButton
                  label="Usuń"
                  confirm={`Usunąć subskrypcję ${s.event} (id ${s.id})?`}
                  action={async () => {
                    const r = await adminApi.kickDeleteSub(s.id);
                    return `Deleted ${r.deleted} via ${r.usingToken}`;
                  }}
                  onDone={refresh}
                  small
                />
              </div>
            ))}
            {status.token.present && !status.token.expired && status.subscriptions.count === 0 && (
              <SubscribeButton kickUserId={status.token.kickUserId} onDone={refresh} />
            )}
          </div>

          <KV
            label="Webhook deliveries (lokalna baza)"
            value={`${status.webhookDeliveries.total} łącznie · ostatnia: ${
              status.webhookDeliveries.lastReceivedAt ? fmtDate(status.webhookDeliveries.lastReceivedAt) : "nigdy"
            }`}
          />
        </div>
      )}
    </section>
  );
}

// ─── Subiekt status ────────────────────────────────────────────
function SubiektStatusSection() {
  const [status, setStatus] = useState<SubiektStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    setErr(null);
    try {
      setStatus(await adminApi.subiektStatus());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <section className="brut bg-carp-paper p-5 space-y-3">
      <header className="flex items-center justify-between gap-3">
        <h2 className="font-display font-bold text-xl">Subiekt 123 — stan integracji</h2>
        <button type="button" className="btn-primary !py-1 !px-3 text-xs" disabled={busy} onClick={refresh}>
          {busy ? "…" : "Odśwież"}
        </button>
      </header>

      {err && <div className="brut bg-carp-destructive text-white p-2 text-xs">{err}</div>}
      {status && (
        <div className="text-sm space-y-3 font-mono">
          <div className="brut bg-carp-yellow p-3 text-xs">
            <p className="font-display font-bold uppercase">Hint</p>
            <p className="mt-1">{status.hint}</p>
          </div>

          <KV
            label="Feature toggle InvoiceIssuingEnabled"
            value={status.featureToggles.invoiceIssuingEnabled ? "WŁĄCZONE" : "wyłączone"}
            tone={status.featureToggles.invoiceIssuingEnabled ? "ok" : "warn"}
          />
          <KV label="Client ID skonfigurowany" value={status.config.clientIdConfigured ? "tak" : "NIE"} />
          <KV label="Subscription key skonfigurowany" value={status.config.subscriptionKeyConfigured ? "tak" : "NIE"} />
          <KV label="API base URL" value={status.config.apiBaseUrl} />
          <KV label="Auto-issue after answered" value={status.config.autoIssueAfterAnswered ? "tak" : "nie"} />

          {status.token.present ? (
            <div className="border-l-4 border-carp-primary pl-3 text-xs">
              <p className="font-display font-bold uppercase">Token</p>
              <p>issued: {fmtDate(status.token.issuedAt)} · expires: {fmtDate(status.token.expiresAt)}</p>
              <p>expired: {status.token.expired ? "TAK" : "nie"} · revoked: {status.token.revoked ? "TAK" : "nie"}</p>
            </div>
          ) : (
            <div className="border-l-4 border-carp-destructive pl-3 text-xs space-y-2">
              <p>Token: <b>brak</b></p>
              <a href={SUBIEKT_CONNECT} className="btn-primary inline-block !py-1 !px-3">
                ▶ Połącz Subiekt teraz
              </a>
            </div>
          )}

          <KV
            label="Wystawione faktury"
            value={`${status.invoicesIssued.count} łącznie${
              status.invoicesIssued.last
                ? ` · ostatnia ${fmtDate(status.invoicesIssued.last.invoiceIssuedAt)} (id ${status.invoicesIssued.last.invoiceId})`
                : ""
            }`}
          />
        </div>
      )}
    </section>
  );
}

// Subscribe button — appears when Kick token is present but subscriptions=0.
// POSTs /api/auth/kick/subscribe?kickUserId=... and triggers status refresh.
function SubscribeButton({ kickUserId, onDone }: { kickUserId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function fire() {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(
        `${API_BASE}/api/auth/kick/subscribe?kickUserId=${encodeURIComponent(kickUserId)}`,
        { method: "POST", credentials: "include" },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-1">
      <button type="button" className="btn-primary !py-1 !px-3" disabled={busy} onClick={fire}>
        {busy ? "Subskrybuję…" : "▶ Subskrybuj events teraz"}
      </button>
      {err && <p className="text-carp-destructive">{err}</p>}
    </div>
  );
}

// Destructive action button. confirm() popup (browser native — UI is admin-only,
// fine), then action() runs and its return string is briefly shown next to
// the button. Used for unsubscribe / revoke / delete-token operations where
// the operator should pause and re-read what they're about to do.
function DangerButton({
  label, confirm, action, onDone, small,
}: {
  label: string;
  confirm: string;
  action: () => Promise<string>;
  onDone: () => void;
  small?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function fire() {
    if (!window.confirm(confirm)) return;
    setBusy(true);
    setMsg(null);
    try {
      const out = await action();
      setMsg(out);
      onDone();
    } catch (e) {
      setMsg(`Błąd: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  const sz = small ? "!py-0.5 !px-2 text-[10px]" : "!py-1 !px-3 text-xs";
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        className={`brut bg-carp-destructive text-white ${sz}`}
        disabled={busy}
        onClick={fire}
      >
        {busy ? "…" : label}
      </button>
      {msg && <span className="text-[10px] text-carp-ink/70">{msg}</span>}
    </span>
  );
}

// ─── helpers ───────────────────────────────────────────────────
function KV({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div className="text-xs">
      <span className="text-carp-ink/60">{label}: </span>
      <b className={tone === "ok" ? "text-carp-lime" : tone === "warn" ? "text-carp-destructive" : ""}>
        {value}
      </b>
    </div>
  );
}

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleString("pl-PL"); }
  catch { return iso; }
}
